import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ArrowRightIcon,
  ChartBarIcon,
  CodeBracketIcon,
  DevicePhoneMobileIcon,
  CloudIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';


import ecommerceImg from '../../assets/img/Recent/ecommerce.png';
import cloudImg from '../../assets/img/Recent/cloud.png';
import mobileImg from '../../assets/img/Recent/Mobilebanking.png';
import analyticsImg from '../../assets/img/Recent/data analysis.png';
import programmingImg from '../../assets/img/Recent/Programing.png';
import cloudMigrationImg from '../../assets/img/Recent/cloud migration.png';

function SectionLabel({ children }) {
  return (
    <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase rounded-full hero-glass-tag text-[#2563EB]">
      {children}
    </span>
  );
}

const stats = [
  { value: '51+', label: 'Projects Delivered' },
  { value: '6', label: 'Industry Verticals' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '9+', label: 'Years Experience' },
];

const categoryIcons = {
  'Web Development': CodeBracketIcon,
  'Mobile Apps': DevicePhoneMobileIcon,
  'Cloud Solutions': CloudIcon,
  Cybersecurity: ShieldCheckIcon,
  'AI & ML': CpuChipIcon,
};

const projects = [
  {
    id: 1,
    title: 'E-Commerce Platform',
    category: 'Web Development',
    image: ecommerceImg,
    description: 'A full-featured e-commerce solution with advanced shopping cart, inventory sync, and global payment integration.',
    technologies: ['React', 'Node.js', 'MongoDB', 'Stripe'],
    client: 'RetailTech Solutions',
    year: '2023',
    span: 'lg:col-span-2 lg:row-span-2',
    featured: true,
  },
  {
    id: 2,
    title: 'Healthcare Management System',
    category: 'Cloud Solutions',
    image: programmingImg,
    description: 'Cloud-based healthcare management with unified patient records, scheduling, and hospital network operations.',
    technologies: ['AWS', 'Python', 'Django', 'PostgreSQL'],
    client: 'MedCare Innovations',
    year: '2023',
    span: '',
  },
  {
    id: 3,
    title: 'Mobile Banking App',
    category: 'Mobile Apps',
    image: mobileImg,
    description: 'Secure mobile banking with biometric auth, real-time transactions, and PCI-DSS compliant architecture.',
    technologies: ['React Native', 'Node.js', 'Firebase'],
    client: 'FinTech Solutions',
    year: '2023',
    span: '',
  },
  {
    id: 4,
    title: 'AI-Powered Analytics Dashboard',
    category: 'AI & ML',
    image: analyticsImg,
    description: 'Executive intelligence platform with predictive forecasting, ML insights, and real-time KPI dashboards.',
    technologies: ['Python', 'TensorFlow', 'React', 'D3.js'],
    client: 'DataSmart Analytics',
    year: '2023',
    span: 'lg:col-span-2',
  },
  {
    id: 5,
    title: 'Cybersecurity Solution',
    category: 'Cybersecurity',
    image: cloudMigrationImg,
    description: 'Enterprise-grade SOC platform with threat detection, SIEM integration, and incident response workflows.',
    technologies: ['Python', 'Kubernetes', 'Docker', 'ELK Stack'],
    client: 'SecureNet Systems',
    year: '2023',
    span: '',
  },
  {
    id: 6,
    title: 'IoT Fleet Management',
    category: 'Cloud Solutions',
    image: cloudImg,
    description: 'Real-time fleet tracking and management powered by IoT sensors, edge analytics, and cloud dashboards.',
    technologies: ['IoT', 'AWS IoT', 'React', 'Node.js'],
    client: 'TransportTech Inc',
    year: '2023',
    span: '',
  },
];

const categories = ['All', 'Web Development', 'Mobile Apps', 'Cloud Solutions', 'Cybersecurity', 'AI & ML'];

const highlights = [
  { icon: ChartBarIcon, title: 'Measurable Impact', desc: 'Every engagement is tracked against KPIs—uptime, conversion, efficiency, and ROI.' },
  { icon: CodeBracketIcon, title: 'Modern Engineering', desc: 'Cloud-native, API-first architectures built for scale, security, and long-term maintainability.' },
  { icon: ShieldCheckIcon, title: 'Security by Design', desc: 'Zero-trust patterns, compliance alignment, and hardened delivery across all verticals.' },
];

export default function Portfolio() {
  const [refHero, inViewHero] = useInView({ threshold: 0.1, triggerOnce: true });
  const [refGrid, inViewGrid] = useInView({ threshold: 0.06, triggerOnce: true });
  const [refHighlights, inViewHighlights] = useInView({ threshold: 0.08, triggerOnce: true });
  const [refCta, inViewCta] = useInView({ threshold: 0.1, triggerOnce: true });

  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects =
    activeCategory === 'All' ? projects : projects.filter((p) => p.category === activeCategory);

  return (
    <motion.div
      layout
      className="bg-white dark:bg-gray-900 text-slate-900 dark:text-white overflow-x-hidden"
    >
      {/* Hero */}
      <section
        ref={refHero}
        className="relative pt-28 pb-16 md:pt-32 md:pb-24 px-6 bg-gradient-to-b from-white via-slate-50/90 to-white dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
        <motion.div layout="position" className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-300/10 rounded-full blur-3xl" />

        <motion.div
          layout="position"
          initial={{ opacity: 0, y: 28 }}
          animate={inViewHero ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <SectionLabel>Case Studies</SectionLabel>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            <span className="text-slate-900 dark:text-white">Our </span>
            <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Portfolio
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            Enterprise solutions engineered with precision—from AI command centers and fintech platforms to secure cloud
            infrastructure and healthcare systems.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 16 }}
                animate={inViewHero ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.12 + i * 0.08 }}
                className="hero-glass-card rounded-2xl px-4 py-5 text-center"
              >
                <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#2563EB] to-cyan-500 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-xs md:text-sm text-slate-500 dark:text-gray-400 mt-1 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          <a
            href="#portfolio-grid"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all"
          >
            Browse Projects <ArrowRightIcon className="w-5 h-5" />
          </a>
        </motion.div>
      </section>

      {/* Filter + Grid */}
      <section id="portfolio-grid" ref={refGrid} className="py-20 md:py-28 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-white to-white dark:from-gray-800/40 dark:via-gray-900 dark:to-gray-900 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            layout="position"
            initial={{ opacity: 0, y: 20 }}
            animate={inViewGrid ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-12"
          >
            <SectionLabel>Featured Work</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Recent <span className="text-[#2563EB]">Projects</span>
            </h2>
            <p className="text-slate-600 dark:text-gray-300 max-w-2xl mx-auto">
              Filter by capability to explore solutions tailored to your industry and technology stack.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-2 mb-12 p-1.5 rounded-2xl hero-glass-card w-fit mx-auto max-w-full">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                  activeCategory === cat ? 'text-[#2563EB]' : 'text-slate-500 hover:text-slate-800 dark:hover:text-gray-200'
                }`}
              >
                {activeCategory === cat && (
                  <motion.div
                    layoutId="portfolio-cat-pill"
                    className="absolute inset-0 bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-slate-100 dark:border-gray-600"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{cat}</span>
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 auto-rows-fr">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project, i) => {
                const CatIcon = categoryIcons[project.category] || CodeBracketIcon;
                const isFeatured = project.span.includes('row-span');

                return (
                  <motion.article
                    layout
                    key={project.id}
                    initial={{ opacity: 0, y: 30, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    whileHover={{ y: -8 }}
                    onClick={() => setSelectedProject(project)}
                    className={`group relative rounded-2xl overflow-hidden hero-glass-card border border-slate-200/90 dark:border-gray-700 cursor-pointer hover:shadow-2xl hover:shadow-blue-500/10 transition-shadow duration-500 ${project.span}`}
                  >
                    <motion.div layout="position" className={`relative overflow-hidden ${isFeatured ? 'min-h-[320px]' : 'h-52'}`}>
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
                      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-white/90 dark:bg-gray-900/80 backdrop-blur text-[#2563EB]">
                        <CatIcon className="w-3.5 h-3.5" />
                        {project.category}
                      </span>
                      {project.featured && (
                        <span className="absolute top-4 right-4 px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-gradient-to-r from-[#2563EB] to-cyan-500 text-white">
                          Featured
                        </span>
                      )}
                      <motion.div
                        layout="position"
                        className="absolute bottom-0 left-0 right-0 p-5 md:p-6 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300"
                      >
                        <p className="text-white/90 text-sm line-clamp-2 mb-3">{project.description}</p>
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300">
                          View details <ArrowRightIcon className="w-3.5 h-3.5" />
                        </span>
                      </motion.div>
                    </motion.div>

                    <div className="p-5 md:p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-slate-400 dark:text-gray-500 font-medium">{project.year}</span>
                        <span className="text-xs text-slate-500 dark:text-gray-400">{project.client}</span>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-[#2563EB] transition-colors">
                        {project.title}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.slice(0, 4).map((tech) => (
                          <span
                            key={tech}
                            className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-300 border border-slate-200/80 dark:border-gray-700"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Highlights */}
      <section ref={refHighlights} className="py-20 md:py-28 px-6 border-t border-slate-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto">
          <motion.div
            layout="position"
            initial={{ opacity: 0, y: 20 }}
            animate={inViewHighlights ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-14"
          >
            <SectionLabel>Our Approach</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              How We <span className="text-[#2563EB]">Deliver</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {highlights.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  layout="position"
                  initial={{ opacity: 0, y: 24 }}
                  animate={inViewHighlights ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="hero-glass-card rounded-2xl p-6 md:p-8 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB]/15 to-cyan-500/10 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        ref={refCta}
        className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden"
      >
        <motion.div
          layout="position"
          initial={{ opacity: 0, y: 20 }}
          animate={inViewCta ? { opacity: 1, y: 0 } : {}}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-white/90 mb-10">
            Let&apos;s architect a solution that matches your ambition—secure, scalable, and built for the future.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-medium rounded-lg shadow-lg hover:bg-gray-100 transition"
          >
            Contact Us <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </motion.div>
      </section>

      {/* Project detail modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              layout
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl hero-glass-card border border-slate-200/90 dark:border-gray-700 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-48 md:h-56 overflow-hidden rounded-t-3xl">
                <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-full object-cover" />
                <motion.div layout="position" className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                <button
                  type="button"
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 text-slate-700 dark:text-gray-200 hover:bg-white transition-colors"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
                <div className="absolute bottom-4 left-6 right-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-cyan-300">{selectedProject.category}</span>
                  <h3 className="text-2xl font-bold text-white mt-1">{selectedProject.title}</h3>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <p className="text-slate-600 dark:text-gray-300 leading-relaxed mb-6">{selectedProject.description}</p>
                <motion.div layout="position" className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <motion.div layout="position" className="rounded-xl bg-slate-50 dark:bg-gray-800/80 p-4">
                    <p className="text-slate-400 dark:text-gray-500 text-xs uppercase tracking-wider mb-1">Client</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{selectedProject.client}</p>
                  </motion.div>
                  <motion.div layout="position" className="rounded-xl bg-slate-50 dark:bg-gray-800/80 p-4">
                    <p className="text-slate-400 dark:text-gray-500 text-xs uppercase tracking-wider mb-1">Year</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{selectedProject.year}</p>
                  </motion.div>
                </motion.div>
                <div className="flex flex-wrap gap-2 mb-8">
                  {selectedProject.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 text-xs font-medium rounded-lg bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <Link
                  to="/contact"
                  onClick={() => setSelectedProject(null)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all"
                >
                  Start a Similar Project <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
